/**
 * Strings of the survey builder (`svy.*`): list of survey templates, editor with preview, results, the board tile, the
 * customer tab block and the ERP settings of notification channels. `svyEn` is typed against `svyPl`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const svyPl = {
  nav: 'Ankiety',
  list: {
    title: 'Ankiety klientów',
    subtitle: 'Szablony ankiet wypełnianych przez handlowców w wizytach — tworzenie, edycja i wyniki',
    new: 'Nowa ankieta',
    empty: 'Brak ankiet — utwórz pierwszą',
    kpiActive: 'Aktywne',
    kpiTemplates: 'Szablony',
    kpiResponses: 'Odpowiedzi',
    questions: '{count} pytań',
    responses: '{count} odpowiedzi',
    lastResponse: 'Ostatnia: {date}',
    noResponses: 'Brak odpowiedzi',
    system: 'systemowa',
    demo: 'dane przykładowe',
    edit: 'Edytuj',
    results: 'Wyniki',
    scopeNote: 'Liczby odpowiedzi obejmują klientów w Twoim zakresie danych.',
  },
  status: { ACTIVE: 'Aktywna', INACTIVE: 'Wyłączona', SCHEDULED: 'Zaplanowana', EXPIRED: 'Po terminie' },
  audience: {
    ALL: 'Wszyscy klienci',
    SEGMENT: 'Segment',
    GROUP: 'Grupa klientów (Subiekt)',
    label: 'Do kogo',
    values: '{kind}: {values}',
    pick: 'Wybierz wartości',
    customers: '{count} klientów',
    custom: 'Inna wartość…',
    add: 'Dodaj',
  },
  editor: {
    titleNew: 'Nowa ankieta',
    titleEdit: 'Edycja ankiety',
    back: 'Ankiety',
    general: 'Ankieta',
    name: 'Tytuł',
    description: 'Opis (widzi handlowiec)',
    active: 'Aktywna — handlowcy widzą ją w wizytach',
    validity: 'Okres ważności',
    validFrom: 'Od',
    validTo: 'Do',
    validityHint: 'Puste = bez ograniczenia.',
    questions: 'Pytania',
    addQuestion: 'Dodaj pytanie',
    question: 'Pytanie {n}',
    label: 'Treść pytania',
    type: 'Typ odpowiedzi',
    required: 'Wymagane',
    options: 'Odpowiedzi do wyboru',
    option: 'Odpowiedź {n}',
    addOption: 'Dodaj odpowiedź',
    min: 'Min.',
    max: 'Maks.',
    unit: 'Jednostka',
    moveUp: 'Przenieś wyżej',
    moveDown: 'Przenieś niżej',
    remove: 'Usuń pytanie',
    removeOption: 'Usuń odpowiedź',
    save: 'Zapisz',
    saved: 'Zapisano ankietę',
    saveFailed: 'Nie udało się zapisać ankiety',
    delete: 'Usuń ankietę',
    deleteConfirm: 'Usunąć ankietę „{title}”? Tej operacji nie można cofnąć.',
    deleted: 'Usunięto ankietę',
    deleteFailed: 'Nie udało się usunąć ankiety',
    systemLocked: 'To szablon systemowy: jego pytania zasilają profil klienta i filtry listy klientów, więc nie można ich zmieniać. Możesz zmienić tytuł, opis, odbiorców, okres i wyłączyć ankietę.',
    usedNote: 'Ankieta ma już {count} odpowiedzi. Zmiana pytań nie zmienia zapisanych odpowiedzi; w wynikach liczą się bieżące pytania.',
    fixErrors: 'Popraw zaznaczone pola',
    preview: 'Podgląd',
    previewHint: 'Tak handlowiec zobaczy ankietę w wizycie.',
    previewEmpty: 'Dodaj pytania, żeby zobaczyć podgląd.',
    loadFailed: 'Nie udało się wczytać ankiety',
  },
  type: { single: 'Jednokrotny wybór', multi: 'Wielokrotny wybór', boolean: 'Tak / nie', text: 'Tekst', number: 'Liczba', date: 'Data', rating: 'Ocena' },
  issue: {
    TITLE_REQUIRED: 'Podaj tytuł ankiety',
    TITLE_TOO_LONG: 'Tytuł jest za długi',
    DESCRIPTION_TOO_LONG: 'Opis jest za długi',
    NO_QUESTIONS: 'Dodaj co najmniej jedno pytanie',
    TOO_MANY_QUESTIONS: 'Za dużo pytań',
    KEY_INVALID: 'Niepoprawny klucz pytania',
    KEY_DUPLICATE: 'Powtórzony klucz pytania',
    TYPE_INVALID: 'Wybierz typ odpowiedzi',
    LABEL_REQUIRED: 'Wpisz treść pytania',
    LABEL_TOO_LONG: 'Treść pytania jest za długa',
    OPTIONS_MIN: 'Dodaj co najmniej dwie odpowiedzi',
    OPTIONS_MAX: 'Za dużo odpowiedzi',
    OPTION_LABEL_REQUIRED: 'Wpisz treść odpowiedzi',
    OPTION_DUPLICATE: 'Odpowiedzi się powtarzają',
    RANGE_INVALID: 'Min. nie może być większe niż maks.',
    AUDIENCE_VALUES_REQUIRED: 'Wybierz co najmniej jedną wartość',
    DATE_INVALID: 'Niepoprawna data',
    VALIDITY_INVALID: 'Data „od” jest późniejsza niż „do”',
  },
  answer: { yes: 'Tak', no: 'Nie', none: '—', pickDate: 'Wybierz datę', typeHere: 'Odpowiedź…' },
  results: {
    title: 'Wyniki ankiety',
    back: 'Ankiety',
    edit: 'Edytuj ankietę',
    kpiResponses: 'Odpowiedzi',
    kpiCustomers: 'Klienci',
    kpiLast: 'Ostatnia odpowiedź',
    from: 'Od',
    to: 'Do',
    clear: 'Wszystkie',
    export: 'Eksport XLSX',
    exporting: 'Przygotowuję plik…',
    exported: 'Pobrano {name}',
    exportFailed: 'Nie udało się przygotować pliku',
    answered: '{count} odpowiedzi',
    avg: 'Średnia',
    min: 'Min.',
    max: 'Maks.',
    dateRange: 'Od {min} do {max}',
    noAnswers: 'Brak odpowiedzi na to pytanie',
    latestTexts: 'Ostatnie odpowiedzi',
    responses: 'Odpowiedzi klientów',
    customer: 'Klient',
    filledAt: 'Data',
    filledBy: 'Wypełnił(a)',
    empty: 'Brak odpowiedzi w wybranym okresie',
    scopeNote: 'Wyniki obejmują klientów w Twoim zakresie danych.',
    prev: 'Poprzednia',
    next: 'Następna',
    page: 'Strona {page} z {pages}',
    loadFailed: 'Nie udało się wczytać wyników',
  },
  tile: {
    title: 'Ankiety klientów',
    hint: 'Kreator ankiet wypełnianych w wizytach',
    active: '{count} aktywnych',
    responses: '{count} odpowiedzi',
    open: 'Otwórz kreator',
  },
  customer: {
    title: 'Ankiety',
    hint: 'Ostatnia odpowiedź każdej ankiety (wypełniane w wizycie w aplikacji handlowca).',
    none: 'Klient nie ma jeszcze odpowiedzi ani ankiet do wypełnienia',
    pending: 'do wypełnienia w wizycie',
    filled: 'Wypełniono {date}',
    by: '· {name}',
    count: '{count} odpowiedzi',
  },
  erp: {
    title: 'Dane z Subiekta (tylko do odczytu)',
    contactType: 'Preferowany kontakt',
    channels: 'Kanały powiadomień',
    invoice: 'Forma faktury (pole własne)',
    notifyEmail: 'E-mail do powiadomień o dostawach',
    documentEmail: 'E-mail do wysyłki dokumentów',
    filled: 'podany',
    notFilled: 'brak',
    contact: { PHONE: 'Telefon', EMAIL: 'E-mail', SMS: 'SMS', VISIT: 'Wizyta', POST: 'Poczta', FAX: 'Faks', OTHER: 'Inny' },
  },
  settings: {
    section: 'Kanały powiadomień i faktura z Subiekta',
    hint: 'Skąd synchronizacja „Zgody klientów” bierze kanały powiadomień i formę faktury. Do NEXOPS trafiają tylko flagi i kanały — nigdy treść pola, numer telefonu ani adres e-mail.',
    contactType: 'Preferowany rodzaj kontaktu z kartoteki kontrahenta jako kanał powiadomień',
    invoiceField: 'Pole własne z formą faktury',
    channelsField: 'Pole własne z kanałami powiadomień',
    notifyEmailField: 'Pole dodatkowe z adresem e-mail do powiadomień o dostawach',
    none: 'Nie czytaj',
    field: 'Pole własne {n}',
    extra: 'Pole dodatkowe {n}',
    afterSave: 'Zmiana działa od następnej synchronizacji „Zgody klientów”.',
  },
};

export const svyEn: Shape<typeof svyPl> = {
  nav: 'Surveys',
  list: {
    title: 'Customer surveys',
    subtitle: 'Survey templates filled in by reps during visits — create, edit and see the results',
    new: 'New survey',
    empty: 'No surveys yet — create the first one',
    kpiActive: 'Active',
    kpiTemplates: 'Templates',
    kpiResponses: 'Responses',
    questions: '{count} questions',
    responses: '{count} responses',
    lastResponse: 'Last: {date}',
    noResponses: 'No responses',
    system: 'system',
    demo: 'sample data',
    edit: 'Edit',
    results: 'Results',
    scopeNote: 'Response counts cover the customers in your data scope.',
  },
  status: { ACTIVE: 'Active', INACTIVE: 'Off', SCHEDULED: 'Scheduled', EXPIRED: 'Expired' },
  audience: {
    ALL: 'All customers',
    SEGMENT: 'Segment',
    GROUP: 'Customer group (Subiekt)',
    label: 'Audience',
    values: '{kind}: {values}',
    pick: 'Pick values',
    customers: '{count} customers',
    custom: 'Other value…',
    add: 'Add',
  },
  editor: {
    titleNew: 'New survey',
    titleEdit: 'Edit survey',
    back: 'Surveys',
    general: 'Survey',
    name: 'Title',
    description: 'Description (shown to the rep)',
    active: 'Active — reps see it during visits',
    validity: 'Valid',
    validFrom: 'From',
    validTo: 'To',
    validityHint: 'Empty = no limit.',
    questions: 'Questions',
    addQuestion: 'Add question',
    question: 'Question {n}',
    label: 'Question text',
    type: 'Answer type',
    required: 'Required',
    options: 'Answers to choose from',
    option: 'Answer {n}',
    addOption: 'Add answer',
    min: 'Min',
    max: 'Max',
    unit: 'Unit',
    moveUp: 'Move up',
    moveDown: 'Move down',
    remove: 'Remove question',
    removeOption: 'Remove answer',
    save: 'Save',
    saved: 'Survey saved',
    saveFailed: 'Could not save the survey',
    delete: 'Delete survey',
    deleteConfirm: 'Delete the survey “{title}”? This cannot be undone.',
    deleted: 'Survey deleted',
    deleteFailed: 'Could not delete the survey',
    systemLocked: 'This is a system template: its questions feed the customer profile and the customer list filters, so they cannot change. You can change the title, description, audience, validity and switch it off.',
    usedNote: 'The survey already has {count} responses. Changing questions does not change saved answers; the results use the current questions.',
    fixErrors: 'Fix the highlighted fields',
    preview: 'Preview',
    previewHint: 'This is how the rep sees the survey during a visit.',
    previewEmpty: 'Add questions to see the preview.',
    loadFailed: 'Could not load the survey',
  },
  type: { single: 'Single choice', multi: 'Multiple choice', boolean: 'Yes / no', text: 'Text', number: 'Number', date: 'Date', rating: 'Rating' },
  issue: {
    TITLE_REQUIRED: 'Enter the survey title',
    TITLE_TOO_LONG: 'The title is too long',
    DESCRIPTION_TOO_LONG: 'The description is too long',
    NO_QUESTIONS: 'Add at least one question',
    TOO_MANY_QUESTIONS: 'Too many questions',
    KEY_INVALID: 'Invalid question key',
    KEY_DUPLICATE: 'Duplicate question key',
    TYPE_INVALID: 'Pick an answer type',
    LABEL_REQUIRED: 'Enter the question text',
    LABEL_TOO_LONG: 'The question text is too long',
    OPTIONS_MIN: 'Add at least two answers',
    OPTIONS_MAX: 'Too many answers',
    OPTION_LABEL_REQUIRED: 'Enter the answer text',
    OPTION_DUPLICATE: 'Answers repeat',
    RANGE_INVALID: 'Min cannot be greater than max',
    AUDIENCE_VALUES_REQUIRED: 'Pick at least one value',
    DATE_INVALID: 'Invalid date',
    VALIDITY_INVALID: '“From” is after “to”',
  },
  answer: { yes: 'Yes', no: 'No', none: '—', pickDate: 'Pick a date', typeHere: 'Answer…' },
  results: {
    title: 'Survey results',
    back: 'Surveys',
    edit: 'Edit survey',
    kpiResponses: 'Responses',
    kpiCustomers: 'Customers',
    kpiLast: 'Last response',
    from: 'From',
    to: 'To',
    clear: 'All',
    export: 'Export XLSX',
    exporting: 'Preparing the file…',
    exported: 'Downloaded {name}',
    exportFailed: 'Could not prepare the file',
    answered: '{count} answers',
    avg: 'Average',
    min: 'Min',
    max: 'Max',
    dateRange: 'From {min} to {max}',
    noAnswers: 'No answers to this question',
    latestTexts: 'Latest answers',
    responses: 'Customer responses',
    customer: 'Customer',
    filledAt: 'Date',
    filledBy: 'Filled in by',
    empty: 'No responses in the selected period',
    scopeNote: 'The results cover the customers in your data scope.',
    prev: 'Previous',
    next: 'Next',
    page: 'Page {page} of {pages}',
    loadFailed: 'Could not load the results',
  },
  tile: {
    title: 'Customer surveys',
    hint: 'Builder of surveys filled in during visits',
    active: '{count} active',
    responses: '{count} responses',
    open: 'Open the builder',
  },
  customer: {
    title: 'Surveys',
    hint: 'The latest response to each survey (filled in during a visit in the rep app).',
    none: 'The customer has no responses and no surveys to fill in yet',
    pending: 'to fill in during a visit',
    filled: 'Filled in {date}',
    by: '· {name}',
    count: '{count} responses',
  },
  erp: {
    title: 'Data from Subiekt (read-only)',
    contactType: 'Preferred contact',
    channels: 'Notification channels',
    invoice: 'Invoice form (custom field)',
    notifyEmail: 'E-mail for delivery notifications',
    documentEmail: 'E-mail for documents',
    filled: 'given',
    notFilled: 'none',
    contact: { PHONE: 'Phone', EMAIL: 'E-mail', SMS: 'SMS', VISIT: 'Visit', POST: 'Mail', FAX: 'Fax', OTHER: 'Other' },
  },
  settings: {
    section: 'Notification channels and invoice form from Subiekt',
    hint: 'Where the “Customer consents” sync takes notification channels and the invoice form from. Only flags and channels reach NEXOPS — never the field text, a phone number or an e-mail address.',
    contactType: 'Preferred contact type of the customer card as a notification channel',
    invoiceField: 'Custom field with the invoice form',
    channelsField: 'Custom field with notification channels',
    notifyEmailField: 'Additional field with the delivery notification e-mail',
    none: 'Do not read',
    field: 'Custom field {n}',
    extra: 'Additional field {n}',
    afterSave: 'The change applies from the next “Customer consents” sync.',
  },
};
